import React, { useEffect } from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {
    Sizes,
    useToggle,
    PrimaryButton,
    PageBehaviourButton,
    AnimatedExpandingContainer,
    FormattedNumberRaisedSymbol,
    ANALYTICS_KEY,
    ANALYTICS_ATTRIBUTE_NAME,
} from '@ratehub/base-ui';

import GICsAccountPropType from '../definitions/GICsAccountPropType';
import ProductTableProviderCell from './ProductTableProviderCell';
import ProductTableBestGICRatesMoreDetails from './ProductTableBestGICRatesMoreDetails';
import ProductTableBestGICRatesLightbox from './ProductTableBestGICRatesLightbox';


function ProductTableBestGICRatesTableCell({
    rate,
    onRateClick,
    isMoreDetailsInLightbox = false,
    className,
    ...otherProps
}) {
    const [ isExpanded, toggleExpanded, setIsExpanded ] = useToggle();

    /* Whenever isMoreDetailsInLightbox changes, we want to collapse
        the “more details” section so that we don’t end up where
        the user’s window size changes and multiple lightboxes are open */
    useEffect(() => {
        setIsExpanded(false);
    }, [ isMoreDetailsInLightbox ]);


    if (!rate) {
        return (
            <p className="rh-text-s rh-fg-stone-darkest">
                <FormattedMessage
                    id="web-components.best-gics.rate-not-available"
                    defaultMessage="No available rate at the moment."
                />
            </p>
        );
    }


    return (
        <div
            className={classNames('rh-display-flex rh-flex-wrap rh-width-100p', className, ANALYTICS_ATTRIBUTE_NAME)}
            ref={self => {
                if (self) {
                    self[ANALYTICS_KEY] = rate;
                }
            }}
            {...otherProps}
        >
            <div className="rate-details-container rh-display-flex rh-flex-wrap rh-align-items-flex-start">
                <div className="rate-cta-container">
                    <PageBehaviourButton
                        className="rate-cta"
                        data-name="best-gic-rates-cta"
                        size="extra-large"
                        onClick={() => onRateClick(rate)}
                    >
                        <FormattedNumberRaisedSymbol
                            value={rate.interest / 100}
                            format="percent2"
                            symbolClassName="rh-title-xs"
                        />
                    </PageBehaviourButton>
                </div>
                <ProductTableProviderCell
                    className="provider-details rh-mt-0_25"
                    rate={rate}
                    isLogoWrapped={false}
                    hasLogoBorder={true}
                />

                {/* More details components */}
                <AnimatedExpandingContainer
                    isVisible={isExpanded && !isMoreDetailsInLightbox}
                    withColour={false}
                    alwaysRenderChildMarkup={true}
                    className="full-width"
                >
                    <ProductTableBestGICRatesMoreDetails
                        minimumInvestment={rate.minimumInvestment}
                        isCDIC={rate.isCDIC}
                        redeemable={rate.redeemable}
                        interestCompoundTerm={rate.interestCompoundTerm}
                        interestPaidTerm={rate.interestPaidTerm}
                    />
                </AnimatedExpandingContainer>

                <If condition={isExpanded && isMoreDetailsInLightbox}>
                    <ProductTableBestGICRatesLightbox
                        rate={rate}
                        onClose={toggleExpanded}
                        onRateClick={onRateClick}
                    />
                </If>

                <PageBehaviourButton
                    onClick={toggleExpanded}
                    shouldLowerCaseMessage={false}
                    size="extra-small"
                    target="blank"
                    data-name="best-gics-toggle-details"
                    className="full-width rh-mt-0_5"
                >
                    <FormattedMessage
                        id="web-components.best-gic-rates.toggle-more-details"
                        defaultMessage="{ isExpanded, select, true {Less details -} other {More details +} }"
                        values={{ isExpanded }}
                    />
                </PageBehaviourButton>
            </div>

            {/* CTA for this table cell */}
            <div className="main-cta-container">
                <PrimaryButton
                    size="small"
                    message={(
                        <FormattedMessage
                            id="web-components.best-gic-rates-cell.cta.get-rate"
                            defaultMessage="Get rate"
                        />
                    )}
                    onClick={() => onRateClick(rate)}
                    data-name="best-gic-rates-main-cta"
                />
            </div>
        </div>
    );
}

ProductTableBestGICRatesTableCell.propTypes = {
    rate: GICsAccountPropType,
    onRateClick: PropTypes.func.isRequired,
    isMoreDetailsInLightbox: PropTypes.bool,
    className: PropTypes.string,
};


export default styled(ProductTableBestGICRatesTableCell)`
    column-gap: ${Sizes.SPACING.HALF};
    row-gap: ${Sizes.SPACING.ONE};

    > .rate-details-container {
        /* We want this to wrap when it reaches ~200px (12.5rem) in width
            but if we’re smaller than that then we should use 100%
            so that we don’t get stuck at a max of 12.5rem if our container is smaller */
        flex-basis: min(12.5rem, 100%);
        /* We want to grow twice as much as the CTA button */
        flex-grow: 2;
        flex-shrink: 0;

        column-gap: ${Sizes.SPACING.HALF};
        row-gap: ${Sizes.SPACING.QUARTER};

        > .rate-cta-container {
            flex: 0 0 ${Sizes.SPACING.SIX};

            > .rate-cta {
                line-height: 1;
            }
        }

        > .provider-details {
            flex: 1 0 ${Sizes.SPACING.SIX};
            align-items: flex-start;    // override
            /* This ensures the buttons below are aligned for each table cell */
            min-height: 3.05rem;
        }

        > .full-width {
            flex: 1 0 100%;
        }
    }

    > .main-cta-container {
        flex: 1 0 6rem;
    }
`;
